import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import type { TrendDiscoveryQuery } from '../../types';
import { TREND_COLORS } from '../../trendTheme';
import { useTrendAdmin } from '../TrendAdminContext';

export default function DiscoveryQueryDialog() {
  const {
    discoveryQueryDialogOpen,
    setDiscoveryQueryDialogOpen,
    draftDiscoveryQuery,
    setDraftDiscoveryQuery,
    busyAction,
    handleSaveDiscoveryQuery,
  } = useTrendAdmin();

  return (
    <Dialog open={discoveryQueryDialogOpen} onClose={() => setDiscoveryQueryDialogOpen(false)} fullWidth maxWidth="md">
      <DialogTitle>{draftDiscoveryQuery._id ? 'Redigera discovery-fråga' : 'Ny discovery-fråga'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Namn"
                value={draftDiscoveryQuery.label || ''}
                onChange={(e) => setDraftDiscoveryQuery((p) => ({ ...p, label: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Nyckel"
                helperText={draftDiscoveryQuery._id ? 'Nyckeln används som stabil identifierare och ändras inte på befintliga frågor.' : 'Unik intern nyckel. Lämna tomt för autoformat från namnet.'}
                value={draftDiscoveryQuery.key || ''}
                disabled={Boolean(draftDiscoveryQuery._id)}
                onChange={(e) => setDraftDiscoveryQuery((p) => ({ ...p, key: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Spår"
                value={draftDiscoveryQuery.track || 'general'}
                onChange={(e) => setDraftDiscoveryQuery((p) => ({ ...p, track: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label="Kategori"
                value={draftDiscoveryQuery.category || 'mixed'}
                onChange={(e) => setDraftDiscoveryQuery((p) => ({ ...p, category: e.target.value }))}
              >
                <MenuItem value="mixed">Blandat</MenuItem>
                <MenuItem value="design">Design</MenuItem>
                <MenuItem value="packaging">Förpackning</MenuItem>
                <MenuItem value="food">Mat</MenuItem>
                <MenuItem value="market">Marknad</MenuItem>
                <MenuItem value="competition">Konkurrens</MenuItem>
                <MenuItem value="consumer">Konsument</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                select
                label="Språk"
                value={draftDiscoveryQuery.language || 'sv'}
                onChange={(e) => setDraftDiscoveryQuery((p) => ({ ...p, language: e.target.value as TrendDiscoveryQuery['language'] }))}
              >
                <MenuItem value="sv">sv</MenuItem>
                <MenuItem value="en">en</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                type="number"
                label="Prioritet"
                value={draftDiscoveryQuery.priority || 50}
                onChange={(e) => setDraftDiscoveryQuery((p) => ({ ...p, priority: Number(e.target.value || 0) }))}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ minHeight: 40, px: 1.5, border: `1px solid ${TREND_COLORS.divider}`, borderRadius: 1 }}>
                <Typography variant="body2">Aktiv i discovery</Typography>
                <Switch
                  checked={draftDiscoveryQuery.active !== false}
                  onChange={(e) => setDraftDiscoveryQuery((p) => ({ ...p, active: e.target.checked }))}
                />
              </Stack>
            </Grid>
          </Grid>
          <TextField
            fullWidth
            multiline
            minRows={5}
            label="Prompt / researchfråga"
            helperText="Skriv vad discovery ska leta efter på webben. Håll frågan konkret men inte för smal."
            value={draftDiscoveryQuery.prompt || ''}
            onChange={(e) => setDraftDiscoveryQuery((p) => ({ ...p, prompt: e.target.value }))}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDiscoveryQueryDialogOpen(false)}>Avbryt</Button>
        <Button
          variant="contained"
          disabled={busyAction === 'saveDiscoveryQuery'}
          onClick={() => void handleSaveDiscoveryQuery()}
        >
          Spara
        </Button>
      </DialogActions>
    </Dialog>
  );
}
